/**
 * ----------------------------------------------------------------------------
 * HOST (DOMAIN) BASED ROUTE CONFIG
 * ----------------------------------------------------------------------------
 * The same build is served on more than one domain (admin.thriwe.com,
 * edge.thriwe.com, ...). Every domain keeps the complete route table, a domain
 * entry only re-points a few paths to a page that already exists in
 * `allRoutes.js`, so each audience lands on the right screen.
 *
 * Example: on edge.thriwe.com, `/login` should open the vendor login that
 * admin.thriwe.com serves on `/v2/vendor/login`.
 *
 * To add a new behaviour:
 *   1. add / extend an entry in DOMAIN_CONFIG below
 *   2. write the alias as  'path the user visits': 'path already in allRoutes.js'
 *      (the alias replaces whatever page used to live on that path)
 *
 * Testing it locally (hostname is `localhost` there):
 *   localStorage.setItem('appDomain', 'edge')  -> reload
 *   localStorage.removeItem('appDomain')       -> back to default
 *   or build with REACT_APP_DOMAIN_KEY=edge
 */

export const DEFAULT_DOMAIN = 'admin';

export const DOMAIN_CONFIG = {
  // Any host carrying an `edge` segment is the vendor facing panel, whatever the rest of the
  // domain is: edge.thriwe.com, stag-edge.thriwe.com, edge-uat.thriwe.com, edge.<new-domain>.
  // `edge` has to be its own dot/dash separated segment, so `knowledge.thriwe.com` is not edge.
  // Listed first on purpose: on a host that could read as both (edge.admin.thriwe.com), edge wins.
  edge: {
    hosts: [/(^|[.-])edge([.-]|$)/],
    routeAliases: {
      '/login': '/v2/vendor/login',
    },
    // Kept on every URL of this domain, not just the landing one: the edge panel is the v2 vendor
    // experience, and `userVersion` in the URL is what puts the `User-Version` header on the API
    // calls (see Containers/Config/index.js). Anything that navigates without it - a hard-coded
    // history.push, a refresh, a pasted link - is corrected by `DomainQueryParams`. Other domains
    // define no defaults and are left alone.
    defaultQueryParams: {
      userVersion: 'v2',
    },
    // ... except on the pre-auth screens. Nobody is signed in there, so there is no user version to
    // announce, and the login/logout calls must not carry the header.
    excludedPaths: [/(^|\/)login/i, /(^|\/)logout/i, /password/i, /verify-otp/i, /role-selection/i],
  },

  // everything else - default admin panel, no overrides
  admin: {
    hosts: [/(^|[.-])admin\.thriwe\.com$/],
    routeAliases: {},
  },
};

const matchesHost = (patterns, host) =>
  (patterns ?? []).some(pattern => (pattern instanceof RegExp ? pattern.test(host) : String(pattern).toLowerCase() === host));

const readLocalOverride = () => {
  try {
    return window?.localStorage?.getItem('appDomain');
  } catch (error) {
    return null;
  }
};

/* Which domain entry is this browser tab running under */
export const getDomainKey = () => {
  const override = process.env.REACT_APP_DOMAIN_KEY || readLocalOverride();
  if (override && DOMAIN_CONFIG[override]) {
    return override;
  }

  const host = (typeof window === 'undefined' ? '' : (window?.location?.hostname ?? '')).toLowerCase();
  const matched = Object.keys(DOMAIN_CONFIG).find(key => matchesHost(DOMAIN_CONFIG[key]?.hosts, host));

  return matched ?? DEFAULT_DOMAIN;
};

export const getDomainConfig = () => DOMAIN_CONFIG[getDomainKey()] ?? DOMAIN_CONFIG[DEFAULT_DOMAIN];

/**
 * Adds the current domain's default query params to a path. A param the caller already put on
 * the path wins, so an explicit value is never overwritten.
 *
 * @param {string} path  e.g. '/v2/vendor-dashboard'
 * @returns {string}     '/v2/vendor-dashboard?userVersion=v2' on edge, unchanged on every other domain
 */
export const withDomainQueryParams = path => {
  const defaults = Object.entries(getDomainConfig()?.defaultQueryParams ?? {});
  if (defaults?.length === 0) {
    return path;
  }

  const [pathname, search = ''] = String(path ?? '').split('?');

  if (isDomainQueryParamExcludedPath(pathname)) {
    return path;
  }

  const params = new URLSearchParams(search);

  defaults.forEach(([key, value]) => {
    if (!params.has(key)) {
      params.set(key, value);
    }
  });

  const query = params.toString();

  return query ? `${pathname}?${query}` : pathname;
};

/**
 * The keys this domain adds by default ('userVersion' on edge, none elsewhere). Useful where a URL
 * has to be compared against a link that carries them - the param is a domain default, not part of
 * what identifies the page, so a URL that lost it still points at the same screen.
 *
 * @returns {string[]}
 */
export const getDomainQueryParamKeys = () => Object.keys(getDomainConfig()?.defaultQueryParams ?? {});

/**
 * Screens the domain's default params are kept off - the login and the other pre-auth pages.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isDomainQueryParamExcludedPath = pathname => {
  const excluded = getDomainConfig()?.excludedPaths ?? [];
  const path = String(pathname ?? '');

  return excluded.some(rule => (rule instanceof RegExp ? rule.test(path) : String(rule).toLowerCase() === path.toLowerCase()));
};

/**
 * The URL a location should be corrected to when it is missing the domain's default params, or
 * `null` when there is nothing to do (no defaults on this domain, an excluded path, or the params
 * are already there). The missing params are appended to the query string as text, so whatever is
 * already on the URL is passed through untouched.
 *
 * @param {string} pathname
 * @param {string} search    e.g. '?page=2'
 * @returns {string|null}
 */
export const getDomainQueryParamsRedirect = (pathname, search = '') => {
  const defaults = Object.entries(getDomainConfig()?.defaultQueryParams ?? {});
  if (defaults?.length === 0 || isDomainQueryParamExcludedPath(pathname)) {
    return null;
  }

  const params = new URLSearchParams(search);
  const missing = defaults.filter(([key]) => !params.has(key));
  if (missing?.length === 0) {
    return null;
  }

  const appended = missing.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
  const query = String(search ?? '').replace(/^\?/, '');

  return `${pathname}?${query ? `${query}&${appended}` : appended}`;
};

/**
 * Puts the domain's default params on the address bar before the app renders, so a refresh, a
 * bookmark or a pasted link starts out with them and the first API call already carries the
 * header. `replaceState` keeps it out of the back history. In-app navigation is handled after
 * this by `DomainQueryParams`.
 */
export const applyDomainQueryParamsToLocation = () => {
  if (typeof window === 'undefined' || !window?.history?.replaceState) {
    return;
  }

  const { pathname, search, hash } = window.location;
  const corrected = getDomainQueryParamsRedirect(pathname, search);

  if (corrected) {
    window.history.replaceState(window.history.state, '', `${corrected}${hash ?? ''}`);
  }
};

/**
 * Reverse of the alias table: takes a path as written in `allRoutes.js` and returns the path this
 * domain actually serves that page on — '/v2/vendor/login' becomes '/login' on edge, and stays
 * '/v2/vendor/login' on every other domain. Use it wherever code navigates to a hard-coded route
 * (the logout redirect, for one) so the user lands on the domain's own URL.
 *
 * @param {string} path  a path registered in `allRoutes.js`
 * @returns {string}     the alias serving it on this domain, or `path` unchanged
 */
export const toDomainPath = path => {
  const [pathname, search = ''] = String(path ?? '').split('?');
  const aliases = Object.entries(getDomainConfig()?.routeAliases ?? {});
  const aliasPath = aliases.find(([, targetPath]) => targetPath === pathname)?.[0];

  if (!aliasPath) {
    return path;
  }

  return search ? `${aliasPath}?${search}` : aliasPath;
};

/**
 * Applies the current domain's aliases to the route groups.
 *
 * @param {Object} routeGroups  { publicRoutes, authProtectedRoutes, authV2ProtectedRoutes }
 * @returns {Object} the same shape, with the aliased paths swapped in
 */
export const applyDomainRouteAliases = routeGroups => {
  const aliases = Object.entries(getDomainConfig()?.routeAliases ?? {});
  if (aliases?.length === 0) {
    return routeGroups;
  }

  const groups = Object.fromEntries(Object.entries(routeGroups).map(([groupName, routes]) => [groupName, [...(routes ?? [])]]));

  aliases.forEach(([aliasPath, targetPath]) => {
    const groupName = Object.keys(groups).find(name => groups[name].some(route => route?.path === targetPath));

    if (!groupName) {
      console.warn(`[domainConfig] alias "${aliasPath}" skipped, no route is registered for "${targetPath}"`);
      return;
    }

    const targetRoute = groups[groupName].find(route => route?.path === targetPath);

    // the alias takes the path over, so drop the page that used to own it
    Object.keys(groups).forEach(name => {
      groups[name] = groups[name].filter(route => route?.path !== aliasPath);
    });

    groups[groupName].push({ ...targetRoute, path: aliasPath });
  });

  return groups;
};
